import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useNavigate } from 'react-router';
import api from '../api/api';

const MIN_QUERY_LENGTH = 2;

export default function Search() {
  const navigate = useNavigate();
  const anchorRef = React.useRef(null);
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setError('');
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError('');

      try {
        const response = await api.get('/api/search', {
          params: { q: trimmedQuery, limit: 6 },
          signal: controller.signal,
        });

        setResults(response.data?.results || []);
        setOpen(true);
      } catch (searchError) {
        if (searchError.name !== 'CanceledError' && searchError.code !== 'ERR_CANCELED') {
          console.error('Global search failed:', searchError);
          setError('Recherche indisponible');
          setResults([]);
          setOpen(true);
        }
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [query]);

  const showPanel = open && query.trim().length >= MIN_QUERY_LENGTH;

  const handleResultClick = (result) => {
    setOpen(false);
    setQuery('');
    navigate(result.route);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ width: { xs: '100%', md: 520 }, maxWidth: '100%' }}>
        <FormControl fullWidth variant="outlined">
          <OutlinedInput
            size="small"
            id="global-search"
            value={query}
            placeholder="Rechercher partout..."
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            startAdornment={
              <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            }
            endAdornment={
              loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null
            }
            inputProps={{
              'aria-label': 'Recherche globale',
            }}
            sx={{ bgcolor: 'background.paper' }}
          />
        </FormControl>

        <Popper
          open={showPanel}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1, width: anchorRef.current?.offsetWidth || 520 }}
        >
          <Paper
            elevation={6}
            sx={{
              mt: 1,
              maxHeight: 420,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {error ? (
              <Typography variant="body2" color="error" sx={{ px: 2, py: 1.5 }}>
                {error}
              </Typography>
            ) : results.length === 0 && !loading ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                Aucun resultat
              </Typography>
            ) : (
              <List dense disablePadding>
                {results.map((result) => (
                  <ListItemButton
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    sx={{ alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.25 }}
                  >
                    <Chip label={result.label} size="small" sx={{ minWidth: 92 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {result.title}
                      </Typography>
                      {result.subtitle ? (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {result.subtitle}
                        </Typography>
                      ) : null}
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
